import React, { useEffect, useState } from "react";
import { AlertTriangle, X, ExternalLink, Copy, Mail } from "lucide-react";
import { adminEmailSmtpTest } from "@/lib/api";

/**
 * AdminHealthBanner — sticky banner that surfaces critical /api/admin/*
 * failures (network/CORS/5xx) with one-click escalation to Emergent Support.
 *
 * Polls every 5 min: `GET /api/admin/email/smtp-test` — surfaces SMTP
 * misconfiguration BEFORE it silently swallows emails (the exact bug class
 * that broke magic-link sends today).
 *
 * Listens to the global `hac:admin-api-error` event that the shared axios
 * client dispatches from `lib/api.js`. Only mounted inside the admin tree,
 * so public visitors never see it.
 */
const SUPPORT_EMAIL = "support@emergent.sh";
const SMTP_POLL_MS = 5 * 60 * 1000;

const AdminHealthBanner = () => {
  const [errors, setErrors] = useState([]);
  const [dismissed, setDismissed] = useState(false);
  const [smtpHealth, setSmtpHealth] = useState(null); // { ok, reason, source, host } | null

  useEffect(() => {
    const handler = (e) => {
      const det = e?.detail;
      if (!det) return;
      setErrors((prev) => [det, ...prev].slice(0, 3));
      setDismissed(false);
    };
    window.addEventListener("hac:admin-api-error", handler);
    return () => window.removeEventListener("hac:admin-api-error", handler);
  }, []);

  // SMTP health polling — runs immediately on mount and every 5 minutes after.
  useEffect(() => {
    let mounted = true;
    const check = async () => {
      try {
        const r = await adminEmailSmtpTest();
        if (mounted) setSmtpHealth(r);
      } catch { /* admin not authenticated yet → skip */ }
    };
    check();
    const id = setInterval(check, SMTP_POLL_MS);
    return () => { mounted = false; clearInterval(id); };
  }, []);

  const smtpBroken = smtpHealth && smtpHealth.ok === false;

  if (dismissed) return null;
  if (errors.length === 0 && !smtpBroken) return null;

  const latest = errors[0];

  // SMTP banner takes priority when there are no API errors — it's a passive
  // health signal whereas API errors are reactive to an admin's actual click.
  if (!latest && smtpBroken) {
    const subject = encodeURIComponent("HorseAppCompare admin: SMTP misconfigured");
    const bodyLines = [
      `Hi Emergent Support,`,
      ``,
      `My HorseAppCompare admin SMTP health check is failing. Details:`,
      ``,
      `Reason: ${smtpHealth.reason || "unknown"}`,
      `Source: ${smtpHealth.source || "(none — no DB doc and no env vars)"}`,
      `Host:   ${smtpHealth.host || "(not set)"}`,
      ``,
      `Until this is fixed all transactional emails (magic-links, claim`,
      `notifications, sponsor-inquiry alerts, refresh-celebration, weekly`,
      `digest) silently fail. Please advise.`,
    ];
    const body = encodeURIComponent(bodyLines.join("\n"));
    const mailto = `mailto:${SUPPORT_EMAIL}?subject=${subject}&body=${body}`;
    return (
      <div className="sticky top-0 z-[60] bg-amber-50 border-b-2 border-amber-300 px-4 py-2.5 shadow-sm" data-testid="admin-health-smtp-banner">
        <div className="max-w-7xl mx-auto flex items-start gap-3 flex-wrap">
          <Mail size={18} className="text-amber-700 shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-bold text-amber-900 text-sm">SMTP not configured — transactional emails will silently fail</span>
              {smtpHealth.source && (
                <span className="text-[10px] font-mono uppercase px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-200">source: {smtpHealth.source}</span>
              )}
            </div>
            <div className="text-xs text-amber-800 mt-1 leading-relaxed">{smtpHealth.reason || "SMTP host or from_email missing."}</div>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            <a
              href={mailto}
              className="text-[11px] inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-amber-600 text-white hover:bg-amber-700 font-semibold"
              data-testid="admin-health-smtp-support"
            >
              Email Support <ExternalLink size={11} />
            </a>
            <button
              onClick={() => setDismissed(true)}
              className="text-amber-800 hover:bg-amber-100 rounded p-1"
              aria-label="Dismiss banner"
              data-testid="admin-health-smtp-dismiss"
            >
              <X size={14} />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // CORS detection heuristic: zero status + likely cross-origin URL
  const looksLikeCors = latest.status === 0 && /^https?:\/\//.test(latest.url || "");
  const kind = latest.status === 0 ? (looksLikeCors ? "CORS / Network" : "Network") : `HTTP ${latest.status}`;

  // Pre-filled Emergent Support email link
  const subject = encodeURIComponent(`HorseAppCompare admin: ${kind} error on ${latest.method} ${latest.url}`);
  const bodyLines = [
    `Hi Emergent Support,`,
    ``,
    `I'm hitting a ${kind} error in my HorseAppCompare admin console. Details:`,
    ``,
    `Timestamp:  ${latest.ts}`,
    `Endpoint:   ${latest.method} ${latest.url}`,
    `Status:     ${latest.status || "no response"}`,
    `ACAO:       ${latest.acao || "(none/not received)"}`,
    `Message:    ${latest.message}`,
    `User agent: ${typeof navigator !== "undefined" ? navigator.userAgent : ""}`,
    ``,
    `Could you help diagnose? Thanks.`,
  ];
  const body = encodeURIComponent(bodyLines.join("\n"));
  const mailto = `mailto:${SUPPORT_EMAIL}?subject=${subject}&body=${body}`;

  const copyDiagnostics = async () => {
    try {
      await navigator.clipboard.writeText(bodyLines.join("\n"));
    } catch { /* clipboard may be blocked — silently ignore */ }
  };

  return (
    <div className="sticky top-0 z-[60] bg-red-50 border-b-2 border-red-300 px-4 py-2.5 shadow-sm" data-testid="admin-health-banner">
      <div className="max-w-7xl mx-auto flex items-start gap-3 flex-wrap">
        <AlertTriangle size={18} className="text-red-600 shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-bold text-red-900 text-sm">{kind} error</span>
            <span className="text-[10px] font-mono uppercase px-1.5 py-0.5 rounded bg-red-100 text-red-700 border border-red-200">
              {latest.method} {latest.url.replace(/^https?:\/\/[^/]+/, "")}
            </span>
            <span className="text-[10px] font-mono text-red-700/70">{latest.ts.split("T")[1]?.slice(0, 8)}Z</span>
          </div>
          <div className="text-xs text-red-800 mt-1 leading-relaxed">
            <strong>{latest.message}</strong>
            {latest.acao && (
              <span className="ml-2 font-mono text-[11px] text-red-700/80">· ACAO: <code>{latest.acao}</code></span>
            )}
            {errors.length > 1 && (
              <span className="ml-2 text-red-700/70">· {errors.length - 1} earlier in this session</span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <button
            onClick={copyDiagnostics}
            className="text-[11px] inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full bg-white border border-red-300 text-red-700 hover:bg-red-50"
            title="Copy diagnostic block to clipboard"
            data-testid="admin-health-copy"
          >
            <Copy size={11} /> Copy
          </button>
          <a
            href={mailto}
            className="text-[11px] inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-red-600 text-white hover:bg-red-700 font-semibold"
            data-testid="admin-health-support"
          >
            Email Support <ExternalLink size={11} />
          </a>
          <button
            onClick={() => setDismissed(true)}
            className="text-red-700 hover:bg-red-100 rounded p-1"
            aria-label="Dismiss banner"
            data-testid="admin-health-dismiss"
          >
            <X size={14} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminHealthBanner;
